export type AssessmentStatement = {
  statement: string;
};

export type CompetitivePressure = { vendor: string; productOrSurface: string };
export type TopOpportunity = {
  title: string;
  description: string;
  expectedImpact: string;
  competitivePressure: CompetitivePressure[];
};
export type CompetitorAnnouncement = { title: string; date: string; description: string; url: string };
export type CompetitorSnapshot = CompetitivePressure & {
  summary: string;
  announcements: CompetitorAnnouncement[];
};

export type CompetitiveAssessment = {
  executiveSummary: string;
  strengths: AssessmentStatement[];
  competitiveGaps: AssessmentStatement[];
  improvementOpportunities: AssessmentStatement[];
  competitorAnnouncements: AssessmentStatement[];
  finalAssessment: AssessmentStatement;
  topOpportunities?: TopOpportunity[];
  competitorSnapshots?: CompetitorSnapshot[];
};

export type AssessmentResult =
  { status: "empty" } | { status: "error"; message: string } | { status: "ready"; data: CompetitiveAssessment };

export type AssessmentTheme = { name: string; pillar: string };

export type AssessmentQueryType = "CompetitiveAssessment" | "TopCustomerNeedsCompetitiveAssessment";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStatement = (value: unknown): value is AssessmentStatement =>
  isRecord(value) && typeof value.statement === "string";

const isStatementList = (value: unknown): value is AssessmentStatement[] =>
  Array.isArray(value) && value.every(isStatement);

const toStatement = ({ statement }: AssessmentStatement): AssessmentStatement => ({ statement });

export const getAssessmentSourceUrl = (value: string): string | undefined => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : undefined;
  } catch (error) {
    if (!(error instanceof TypeError) && !(isRecord(error) && error.name === "TypeError")) throw error;
    return undefined;
  }
};

type RawPressure = { vendor: string; product_or_surface: string };
type RawOpportunity = {
  title: string;
  description: string;
  expected_impact: string;
  competitive_pressure: RawPressure[];
};
type RawSnapshot = RawPressure & { summary: string; [key: string]: unknown };

const isPressure = (value: unknown): value is RawPressure & Record<string, unknown> =>
  isRecord(value) && typeof value.vendor === "string" && typeof value.product_or_surface === "string";

const isOpportunity = (value: unknown): value is RawOpportunity =>
  isRecord(value) &&
  typeof value.title === "string" &&
  typeof value.description === "string" &&
  typeof value.expected_impact === "string" &&
  Array.isArray(value.competitive_pressure) &&
  value.competitive_pressure.every(isPressure);

const isSnapshot = (value: unknown): value is RawSnapshot =>
  isRecord(value) &&
  isPressure(value) &&
  typeof value.summary === "string" &&
  [1, 2].every((index) =>
    ["title", "date", "description", "url"].every((field) => {
      const entry = value[`announcement${index}_${field}`];
      return entry === undefined || entry === null || typeof entry === "string";
    })
  );

const numberedItems = (value: Record<string, unknown>, prefix: string): unknown[] =>
  Object.keys(value)
    .filter((key) => new RegExp(`^${prefix}[1-9]\\d*$`).test(key))
    .sort((a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)))
    .map((key) => value[key]);

const toPressure = (value: RawPressure): CompetitivePressure => ({
  vendor: value.vendor,
  productOrSurface: value.product_or_surface
});

const snapshotText = (item: RawSnapshot, key: string): string => {
  const value = item[key];
  return typeof value === "string" ? value : "";
};

const parseOpportunitiesAssessment = (parsed: Record<string, unknown>): AssessmentResult => {
  const overall = parsed.OverallAssessment ?? parsed.overall_assessment;
  const summary = typeof overall === "string" ? overall : isStatement(overall) ? overall.statement : undefined;
  const opportunities =
    parsed.TopOpportunities !== undefined
      ? parsed.TopOpportunities
      : parsed.top_opportunities !== undefined
        ? parsed.top_opportunities
        : numberedItems(parsed, "Opportunity");
  const snapshots =
    parsed.CompetitorSnapshots !== undefined
      ? parsed.CompetitorSnapshots
      : parsed.competitor_snapshots !== undefined
        ? parsed.competitor_snapshots
        : numberedItems(parsed, "Competitor");
  if (
    summary === undefined ||
    !Array.isArray(opportunities) ||
    !opportunities.every(isOpportunity) ||
    !Array.isArray(snapshots) ||
    !snapshots.every(isSnapshot)
  ) {
    return { status: "error", message: "Competitive assessment data has an unexpected format." };
  }
  return {
    status: "ready",
    data: {
      executiveSummary: summary,
      strengths: [],
      competitiveGaps: [],
      improvementOpportunities: [],
      competitorAnnouncements: [],
      finalAssessment: { statement: "" },
      topOpportunities: opportunities.map((item) => ({
        title: item.title,
        description: item.description,
        expectedImpact: item.expected_impact,
        competitivePressure: item.competitive_pressure.map(toPressure)
      })),
      competitorSnapshots: snapshots.map((item) => ({
        ...toPressure(item),
        summary: item.summary,
        announcements: [1, 2]
          .map((index) => ({
            title: snapshotText(item, `announcement${index}_title`),
            date: snapshotText(item, `announcement${index}_date`),
            description: snapshotText(item, `announcement${index}_description`),
            url: snapshotText(item, `announcement${index}_url`)
          }))
          .filter((announcement) => Object.values(announcement).some((field) => field.trim().length > 0))
      }))
    }
  };
};

export const parseCompetitiveAssessment = (
  value: unknown,
  queryType: AssessmentQueryType = "CompetitiveAssessment"
): AssessmentResult => {
  if (value === null || value === undefined || value === "") return { status: "empty" };

  let parsed: unknown = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      return { status: "error", message: "Competitive assessment data contains invalid JSON." };
    }
  }
  if (!isRecord(parsed)) {
    return { status: "error", message: "Competitive assessment data must be a JSON object." };
  }

  if (
    ["TopOpportunities", "top_opportunities", "CompetitorSnapshots", "competitor_snapshots"].some((key) => key in parsed) ||
    Object.keys(parsed).some((key) => /^(Opportunity|Competitor)[1-9]\d*$/.test(key))
  ) {
    return parseOpportunitiesAssessment(parsed);
  }

  const strengths = parsed.Strengths ?? parsed.Stengths;
  const competitiveGaps = parsed.CompetitiveGaps ?? parsed.CompetetiveGaps;
  const finalAssessment = parsed.FinalAssessment ?? parsed.FinalAssesment;
  const competitorAnnouncements = parsed.CompetitorAnnouncements === undefined ? [] : parsed.CompetitorAnnouncements;
  if (
    typeof parsed.ExecutiveSummary !== "string" ||
    !isStatementList(strengths) ||
    !isStatementList(competitiveGaps) ||
    !isStatementList(parsed.ImprovementOpportunities) ||
    (queryType === "CompetitiveAssessment" && !isStatementList(competitorAnnouncements)) ||
    !isStatement(finalAssessment)
  ) {
    return { status: "error", message: "Competitive assessment data has an unexpected format." };
  }

  return {
    status: "ready",
    data: {
      executiveSummary: parsed.ExecutiveSummary,
      strengths: strengths.map(toStatement),
      competitiveGaps: competitiveGaps.map(toStatement),
      improvementOpportunities: parsed.ImprovementOpportunities.map(toStatement),
      // Theme assessments may contain source records instead of displayable announcement statements.
      competitorAnnouncements: isStatementList(competitorAnnouncements) ? competitorAnnouncements.map(toStatement) : [],
      finalAssessment: toStatement(finalAssessment)
    }
  };
};

export const selectCompetitiveAssessment = (
  rows: readonly {
    productName: string | null;
    dataStatus: unknown;
    feedbackPeriod?: string | null;
    themeName?: string | null;
    themePillar?: string | null;
  }[],
  product: string,
  theme?: AssessmentTheme,
  queryType: AssessmentQueryType = "CompetitiveAssessment"
): AssessmentResult => {
  const matchingRows = rows.filter(
    (row) =>
      row.productName === product &&
      (row.feedbackPeriod === undefined || row.feedbackPeriod === null || row.feedbackPeriod === "AllUp") &&
      (!theme || (row.themeName === theme.name && (row.themePillar?.trim() || "Other") === theme.pillar))
  );
  if (matchingRows.length === 0) return { status: "empty" };
  if (matchingRows.length > 1) {
    return {
      status: "error",
      message: theme
        ? "Multiple competitive assessments were returned for the selected product, pillar, and theme."
        : "Multiple competitive assessments were returned for the selected product."
    };
  }
  return parseCompetitiveAssessment(matchingRows[0].dataStatus, queryType);
};
